import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiBriefcase,
  FiPlus,
  FiTrash2,
  FiVideo,
  FiImage,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiUploadCloud,
  FiPlay,
  FiAlertCircle,
} from 'react-icons/fi';
import { MdCloudUpload } from 'react-icons/md';
import { Button } from '../ui/Button';
import { FormLabel, OptionalBadge } from '../ui/FormLabel';
import { CollaboratorsSelector } from './CollaboratorsSelector';
import { DeleteMediaModal } from './DeleteMediaModal';
import { ServicesModal } from '../common/ServicesModal';
import { EventsModal } from '../common/EventsModal';
import {
  UPLOAD_MAX_FILE_SIZE_BYTES,
  UPLOAD_MAX_FILE_SIZE_MB,
  UPLOAD_MAX_MEDIA_FILES,
  formatFileSize,
  getUploadSizeLimitMessage,
  getUploadSizeLimitSkipMessage,
} from '../../utils/uploadValidation';
import { MEDIA_FALLBACK_IMAGE_SRC } from '../../utils/mediaFallback';
import { formatServicePrice } from '../../utils/price';
import {
  validatePlatformUrl,
  getPlatformUrlError,
  type PlatformKey,
} from '../../utils/platformUrlValidation';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectedService {
  id: number;
  title: string;
  description?: string;
  price?: string;
}

export interface SelectedEvent {
  id: number;
  name: string;
  location?: string;
  start?: string;
  image?: string;
}

export interface MediaPlatformLinks {
  website: string;
  spotify: string;
  soundcloud: string;
  appleMusic: string;
  amazonMusic: string;
  youtubeMusic: string;
}

export interface MediaFormValues {
  /** New local File objects to upload */
  files: File[];
  /** Existing remote URLs (populated in edit mode) */
  existingUrls: string[];
  /** 'image' or 'video' — cannot mix both */
  mediaType: 'image' | 'video' | null;
  title: string;
  caption: string;
  service: SelectedService | null;
  event: SelectedEvent | null;
  collaboratorIds: number[];
  platformLinks: MediaPlatformLinks;
}

const EMPTY_PLATFORM_LINKS: MediaPlatformLinks = {
  website: '',
  spotify: '',
  soundcloud: '',
  appleMusic: '',
  amazonMusic: '',
  youtubeMusic: '',
};

export interface MediaFormProps {
  title: string;
  submitLabel: string;
  /** Called with the final form values so the parent can call the mutation */
  onSubmit: (values: MediaFormValues) => void | Promise<void>;
  onDelete?: () => void;
  showDelete?: boolean;
  isLoading?: boolean;
  /** True while the delete mutation is in-flight — disables delete buttons and shows spinner in modal */
  isDeleting?: boolean;
  /** Pre-fill data for edit mode */
  initialData?: {
    title?: string;
    caption?: string;
    existingUrls?: string[];
    mediaType?: 'image' | 'video';
    service?: SelectedService | null;
    event?: SelectedEvent | null;
    collaboratorIds?: number[];
    /** Full collaborator objects from the API (for pre-populating edit mode) */
    initialCollaborators?: Array<{
      id: number;
      full_name?: string;
      username?: string;
      prof_pics_url?: string | null;
    }>;
    platformLinks?: Partial<MediaPlatformLinks>;
  };
  /** Called when the dirty state (has unsaved changes) changes */
  onDirtyChange?: (isDirty: boolean) => void;
}

const MAX_FILES = UPLOAD_MAX_MEDIA_FILES;
const MAX_CAPTION = 10_000;
const MAX_FILE_SIZE_MB = UPLOAD_MAX_FILE_SIZE_MB;
const MAX_FILE_SIZE_BYTES = UPLOAD_MAX_FILE_SIZE_BYTES;

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const ALLOWED_VIDEO_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
]);
const ALLOWED_TYPES = new Set([...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.avi') ||
    lower.endsWith('.mkv') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.m4v') ||
    lower.includes('/video/')
  );
}

function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.round((date.getTime() - now.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff > 1 && diff < 7) return `${diff} days`;
  return date.toLocaleDateString();
}

// ─── Platform link input ──────────────────────────────────────────────────────

interface PlatformInputProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string | null;
}

const PlatformInput: React.FC<PlatformInputProps> = ({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
}) => (
  <div className="flex flex-col gap-2">
    <span className="text-zinc-400 text-[14px] font-medium">{label}</span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-invalid={!!error}
      className={`w-full bg-zinc-900/40 border rounded-2xl px-4 py-3.5 text-white text-[14px] placeholder:text-zinc-600 focus:outline-none focus:ring-1 transition-all ${
        error ? 'border-red-500/60 focus:ring-red-500' : 'border-white/15 focus:ring-blue-500'
      }`}
    />
    {error && <span className="text-red-400 text-[12px]">{error}</span>}
  </div>
);

// ─── Unified media item ──────────────────────────────────────────────────────
type MediaItem =
  | { kind: 'existing'; url: string; id: string }
  | { kind: 'new'; file: File; blobUrl: string; thumbnail: string | null; id: string };

// ─── Component ────────────────────────────────────────────────────────────────

export const MediaForm: React.FC<MediaFormProps> = ({
  title,
  submitLabel,
  onSubmit,
  onDelete,
  showDelete = false,
  isLoading = false,
  isDeleting = false,
  initialData,
  onDirtyChange,
}) => {
  const sectionHeadingClass = 'text-zinc-400 text-base font-semibold';

  // ── unified media list (existing urls + new files in one ordered array) ──
  const [mediaItems, setMediaItems] = useState<MediaItem[]>(() =>
    (initialData?.existingUrls ?? []).map((url) => ({
      kind: 'existing' as const,
      url,
      id: crypto.randomUUID(),
    })),
  );
  // lightbox for video preview
  const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);

  // ── form state ──
  const [titleValue, setTitleValue] = useState(initialData?.title ?? '');
  const [caption, setCaption] = useState(initialData?.caption ?? '');
  const [service, setService] = useState<SelectedService | null>(initialData?.service ?? null);
  const [event, setEvent] = useState<SelectedEvent | null>(initialData?.event ?? null);
  const [collaboratorIds, setCollaboratorIds] = useState<number[]>(
    initialData?.collaboratorIds ?? [],
  );
  const [platformLinks, setPlatformLinks] = useState<MediaPlatformLinks>({
    ...EMPTY_PLATFORM_LINKS,
    ...initialData?.platformLinks,
  });
  const [platformErrors, setPlatformErrors] = useState<
    Partial<Record<keyof MediaPlatformLinks, string>>
  >({});

  const validatePlatformField = useCallback((key: PlatformKey, value: string) => {
    const isValid = validatePlatformUrl(key, value);
    setPlatformErrors((prev) => {
      if (isValid) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: getPlatformUrlError(key) };
    });
    return isValid;
  }, []);

  // ── drag-and-drop state ──
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  // ── modal state ──
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);

  // ── error ──
  const [error, setError] = useState<string | null>(null);

  // ── hidden file inputs ──
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const platformLinksRef = useRef<HTMLDivElement>(null);

  // ── track dirty state ──
  useEffect(() => {
    if (!onDirtyChange || !initialData) return;

    const initialUrls = initialData.existingUrls ?? [];
    const currentUrls = mediaItems
      .filter((m): m is Extract<MediaItem, { kind: 'existing' }> => m.kind === 'existing')
      .map((m) => m.url);
    const hasNewFiles = mediaItems.some((m) => m.kind === 'new');
    const urlsChanged =
      hasNewFiles ||
      currentUrls.length !== initialUrls.length ||
      currentUrls.some((u, i) => u !== initialUrls[i]);

    const titleChanged = titleValue !== (initialData.title ?? '');
    const captionChanged = caption !== (initialData.caption ?? '');
    const serviceChanged = (service?.id ?? null) !== (initialData.service?.id ?? null);
    const eventChanged = (event?.id ?? null) !== (initialData.event?.id ?? null);

    const initialCollabIds = (initialData.collaboratorIds ?? []).slice().sort((a, b) => a - b);
    const currentCollabIds = collaboratorIds.slice().sort((a, b) => a - b);
    const collaboratorsChanged =
      JSON.stringify(initialCollabIds) !== JSON.stringify(currentCollabIds);

    const initialPlatformLinks = { ...EMPTY_PLATFORM_LINKS, ...initialData.platformLinks };
    const platformLinksChanged =
      JSON.stringify(platformLinks) !== JSON.stringify(initialPlatformLinks);

    const isDirty =
      urlsChanged ||
      titleChanged ||
      captionChanged ||
      serviceChanged ||
      eventChanged ||
      collaboratorsChanged ||
      platformLinksChanged;
    onDirtyChange(isDirty);
  }, [
    mediaItems,
    titleValue,
    caption,
    service,
    event,
    collaboratorIds,
    platformLinks,
    initialData,
    onDirtyChange,
  ]);

  const totalCount = mediaItems.length;
  const canAddMore = totalCount < MAX_FILES;
  const captionNearLimit = caption.length >= MAX_CAPTION * 0.9;

  const totalUploadSize = mediaItems
    .filter((item): item is Extract<MediaItem, { kind: 'new' }> => item.kind === 'new')
    .reduce((sum, item) => sum + item.file.size, 0);

  // ── video thumbnail generation ────────────────────────────────────────────

  const generateVideoThumbnail = useCallback((blobUrl: string, itemId: string) => {
    const vid = document.createElement('video');
    vid.src = blobUrl;
    vid.muted = true;
    vid.preload = 'metadata';
    vid.playsInline = true;
    const capture = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 320;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(vid, 0, 0, 320, 320);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setMediaItems((prev) =>
            prev.map((m) =>
              m.kind === 'new' && m.id === itemId ? { ...m, thumbnail: dataUrl } : m,
            ),
          );
        }
      } catch {
        /* cross-origin or codec issue — fall back to video element */
      }
    };
    vid.addEventListener(
      'loadeddata',
      () => {
        vid.currentTime = 0.5;
      },
      { once: true },
    );
    vid.addEventListener('seeked', capture, { once: true });
    // If seek fires before loadeddata (e.g. already buffered), capture directly
    vid.addEventListener(
      'loadedmetadata',
      () => {
        if (vid.readyState >= 2) capture();
      },
      { once: true },
    );
    vid.load();
  }, []);

  // ── add files ────────────────────────────────────────────────────────────

  const addFiles = useCallback(
    (incoming: FileList | File[] | null) => {
      if (!incoming) return;
      setError(null);

      const arr = Array.from(incoming as ArrayLike<File>);

      // ── Format validation ──────────────────────────────────────────────
      const invalid = arr.filter((f) => !ALLOWED_TYPES.has(f.type));
      const validFormat = arr.filter((f) => ALLOWED_TYPES.has(f.type));
      if (invalid.length > 0) {
        setError(
          validFormat.length === 0
            ? 'Unsupported media format. Supported: JPEG, PNG, WebP, GIF, MP4, MOV, AVI, WebM.'
            : `${invalid.length} file${invalid.length > 1 ? 's have' : ' has'} an unsupported format and ${invalid.length > 1 ? 'were' : 'was'} skipped.`,
        );
        if (validFormat.length === 0) return;
      }

      // ── Size validation ───────────────────────────────────────────────────
      const tooLarge = validFormat.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
      const valid = validFormat.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
      if (tooLarge.length > 0) {
        setError(
          valid.length === 0
            ? getUploadSizeLimitMessage(MAX_FILE_SIZE_MB)
            : getUploadSizeLimitSkipMessage(tooLarge.length, MAX_FILE_SIZE_MB),
        );
        if (valid.length === 0) return;
      }

      const remaining = MAX_FILES - totalCount;
      if (remaining <= 0) {
        setError(
          "You've reached the media upload limit (5 files max). Remove some before adding more.",
        );
        return;
      }

      const allowed = valid.slice(0, remaining);
      if (valid.length > remaining) {
        setError(
          `Only the first ${remaining} file${remaining > 1 ? 's were' : ' was'} added — 5 file limit reached.`,
        );
      }

      const newItems: MediaItem[] = allowed.map((f) => {
        const blobUrl = URL.createObjectURL(f);
        const id = crypto.randomUUID();
        if (isVideoFile(f)) generateVideoThumbnail(blobUrl, id);
        return { kind: 'new' as const, file: f, blobUrl, thumbnail: null, id };
      });
      setMediaItems((prev) => [...prev, ...newItems]);
    },
    [totalCount, generateVideoThumbnail],
  );

  // ── remove media ─────────────────────────────────────────────────────────

  // ── remove & reorder (unified across images and videos) ──────────────────

  const removeItem = (id: string) => {
    setMediaItems((prev) => {
      const item = prev.find((m) => m.id === id);
      if (item?.kind === 'new') URL.revokeObjectURL(item.blobUrl);
      return prev.filter((m) => m.id !== id);
    });
  };

  const moveItem = (id: string, direction: 'left' | 'right') => {
    setMediaItems((prev) => {
      const idx = prev.findIndex((m) => m.id === id);
      if (idx < 0) return prev;
      const toIdx = direction === 'left' ? idx - 1 : idx + 1;
      if (toIdx < 0 || toIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[toIdx]] = [next[toIdx], next[idx]];
      return next;
    });
  };

  // ── drag-and-drop handlers ────────────────────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length > 0) addFiles(dropped);
    },
    [addFiles],
  );

  // ── submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mediaItems.length === 0) {
      setError('Please upload at least one media file.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const platformKeys = Object.keys(platformLinks) as PlatformKey[];
    const invalidKeys = platformKeys.filter(
      (key) => !validatePlatformField(key, platformLinks[key]),
    );
    if (invalidKeys.length > 0) {
      platformLinksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const newItems = mediaItems.filter(
      (m): m is Extract<MediaItem, { kind: 'new' }> => m.kind === 'new',
    );
    const existingItems = mediaItems.filter(
      (m): m is Extract<MediaItem, { kind: 'existing' }> => m.kind === 'existing',
    );
    const files = newItems.map((m) => m.file);
    const existingUrls = existingItems.map((m) => m.url);
    const onlyVideos =
      files.every(isVideoFile) && existingUrls.every(isVideoUrl) && mediaItems.length > 0;
    const finalMediaType: 'image' | 'video' = onlyVideos ? 'video' : 'image';
    await onSubmit({
      files,
      existingUrls,
      mediaType: finalMediaType,
      title: titleValue,
      caption,
      service,
      event,
      collaboratorIds,
      platformLinks,
    });
  };

  // ── render helpers ───────────────────────────────────────────────────────

  const renderMediaGrid = () => {
    const items: React.ReactNode[] = [];

    mediaItems.forEach((item, i) => {
      const isVid = item.kind === 'existing' ? isVideoUrl(item.url) : isVideoFile(item.file);

      // Resolve what to show in the cell
      let thumbSrc: string | null = null;
      let videoSrc: string | null = null;
      if (item.kind === 'existing') {
        if (isVid) {
          // Cloudinary first-frame thumbnail via extension swap
          thumbSrc = item.url.replace(/\.(mp4|mov|webm|avi|m4v|mkv|quicktime)(\?.*)?$/i, '.jpg$2');
          videoSrc = item.url;
        } else {
          thumbSrc = item.url;
        }
      } else {
        if (isVid) {
          thumbSrc = item.thumbnail; // canvas still; null while generating
          videoSrc = item.blobUrl;
        } else {
          thumbSrc = item.blobUrl;
        }
      }

      items.push(
        <div
          key={item.id}
          className="relative w-full aspect-square rounded-2xl overflow-hidden bg-zinc-900 group/card"
        >
          {/* Thumbnail / first-frame / video fallback */}
          {thumbSrc ? (
            <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
          ) : videoSrc ? (
            <video
              src={videoSrc}
              className="w-full h-full object-cover"
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                (e.target as HTMLVideoElement).currentTime = 0.001;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-700">
              <FiVideo size={28} />
            </div>
          )}

          {/* Video: play button overlay — click to open full preview */}
          {isVid && videoSrc && (
            <button
              type="button"
              onClick={() => setPreviewItem(item)}
              className="absolute inset-0 flex items-center justify-center z-10"
            >
              <div className="w-11 h-11 rounded-full bg-black/60 flex items-center justify-center group-hover/card:scale-110 transition-transform">
                <FiPlay size={17} className="text-white ml-0.5" />
              </div>
            </button>
          )}

          {/* Remove */}
          <button
            type="button"
            onClick={() => removeItem(item.id)}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500 transition-colors z-20"
          >
            <FiX size={14} />
          </button>

          {/* Reorder arrows — works across the whole unified list */}
          {mediaItems.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20">
              <button
                type="button"
                onClick={() => moveItem(item.id, 'left')}
                disabled={i === 0}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-25 hover:bg-white/20 transition-colors"
              >
                <FiChevronLeft size={13} />
              </button>
              <button
                type="button"
                onClick={() => moveItem(item.id, 'right')}
                disabled={i === mediaItems.length - 1}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-25 hover:bg-white/20 transition-colors"
              >
                <FiChevronRight size={13} />
              </button>
            </div>
          )}

          {item.kind === 'new' && (
            <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/65 text-[10px] text-zinc-100 z-20">
              {formatFileSize(item.file.size)}
            </div>
          )}
        </div>,
      );
    });

    // "Add more" slot
    if (canAddMore) {
      items.push(
        <div
          key="add-more"
          className="w-full aspect-square rounded-2xl border-2 border-dashed border-white/20 bg-zinc-900/40 overflow-hidden flex flex-col"
        >
          {/* Photo half */}
          <button
            type="button"
            onClick={() => {
              setError(null);
              photoInputRef.current?.click();
            }}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 active:scale-95 transition-all group/photo border-b border-white/10 cursor-pointer"
          >
            <FiImage size={22} className="transition-transform group-hover/photo:scale-110" />
            <span className="text-[11px] font-semibold tracking-wide text-center leading-tight">
              Click to add
              <br />
              more Photo
            </span>
          </button>
          {/* Video half */}
          <button
            type="button"
            onClick={() => {
              setError(null);
              videoInputRef.current?.click();
            }}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 active:scale-95 transition-all group/video cursor-pointer"
          >
            <FiVideo size={22} className="transition-transform group-hover/video:scale-110" />
            <span className="text-[11px] font-semibold tracking-wide text-center leading-tight">
              Click to add
              <br />
              more Video
            </span>
          </button>
        </div>,
      );
    }

    return items;
  };

  const hasMedia = totalCount > 0;

  return (
    <div className="max-w-[1440px] mx-auto px-4 pt-2 pb-8">
      {/* Hidden file inputs */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
        onClick={(e) => ((e.target as HTMLInputElement).value = '')}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
        onClick={(e) => ((e.target as HTMLInputElement).value = '')}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* ── Title row — full width, Finish button at far right ── */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-white text-2xl sm:text-[32px] font-bold">{title}</h1>
          <div className="hidden sm:flex items-center gap-4">
            <Button
              type="submit"
              size="lg"
              disabled={isLoading}
              className="rounded-full px-8 py-3 bg-white text-black hover:bg-zinc-200 focus:ring-0 disabled:opacity-50"
            >
              {isLoading ? 'Saving…' : submitLabel}
            </Button>
            {showDelete && (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="rounded-full px-8 py-3 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0 disabled:opacity-50"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                    Deleting…
                  </span>
                ) : (
                  'Delete'
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-400 text-sm">
            <FiAlertCircle size={18} className="shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="shrink-0 p-0.5 rounded-full hover:bg-red-500/20 transition-colors"
              aria-label="Dismiss error"
            >
              <FiX size={16} />
            </button>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-12">
          {/* ── Left Column ─────────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col gap-10">
            {/* ── Media upload section ─────────────────────────────────── */}
            <div
              className={`relative transition-all rounded-3xl ${isDragging ? 'ring-2 ring-blue-500' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="mb-3">
                <FormLabel required className="w-fit">
                  Media Upload
                </FormLabel>
              </div>
              {/* Drag-and-drop overlay */}
              {isDragging && (
                <div className="absolute inset-0 z-10 rounded-3xl border-2 border-dashed border-blue-500 bg-blue-500/10 flex flex-col items-center justify-center gap-3 pointer-events-none">
                  <FiUploadCloud size={40} className="text-blue-400" />
                  <p className="text-blue-400 font-semibold text-sm">Drop files to upload</p>
                  <p className="text-blue-400/60 text-xs">JPEG · PNG · WebP · MP4 · MOV</p>
                </div>
              )}

              {!hasMedia ? (
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    {/* Add Photo button */}
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        photoInputRef.current?.click();
                      }}
                      className="flex-1 h-37.5 rounded-3xl border-2 border-dashed border-white/15 bg-zinc-900/40 hover:bg-white/5 hover:border-white/25 transition-all flex flex-col items-center justify-center gap-3 text-zinc-600 hover:text-white group"
                    >
                      <FiImage size={36} className="group-hover:text-white transition-colors" />
                      <span className="text-sm font-medium">Add Photo</span>
                    </button>
                    {/* Add Video button */}
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        videoInputRef.current?.click();
                      }}
                      className="flex-1 h-37.5 rounded-3xl border-2 border-dashed border-white/15 bg-zinc-900/40 hover:bg-white/5 hover:border-white/25 transition-all flex flex-col items-center justify-center gap-3 text-zinc-600 hover:text-white group"
                    >
                      <FiVideo size={36} className="group-hover:text-white transition-colors" />
                      <span className="text-sm font-medium">Add Video</span>
                    </button>
                  </div>
                  <p className="text-zinc-600 text-xs text-center">
                    Drag &amp; drop or click — JPEG · PNG · WebP · MP4 · MOV &nbsp;•&nbsp; max{' '}
                    {MAX_FILE_SIZE_MB} MB per file &nbsp;•&nbsp; up to {MAX_FILES} files
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="grid grid-cols-3 gap-2">{renderMediaGrid()}</div>
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span>
                      {totalCount}/{MAX_FILES} files
                      {totalUploadSize > 0 && (
                        <span className="ml-1.5">• {formatFileSize(totalUploadSize)} total</span>
                      )}
                    </span>
                    <span>drag &amp; drop to add more · use arrows to reorder</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Title ────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-4">
              <FormLabel optional className="w-fit">
                Title
              </FormLabel>
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  placeholder="Add title..."
                  className="w-full bg-zinc-900/40 border border-white/25 rounded-3xl p-5 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all text-base"
                />
              </div>
            </div>

            {/* ── Caption ──────────────────────────────────────────────── */}
            <div className="flex flex-col gap-4">
              <FormLabel optional className="w-fit">
                Caption
              </FormLabel>
              <div className="flex flex-col gap-1">
                <textarea
                  value={caption}
                  maxLength={MAX_CAPTION}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add caption..."
                  rows={4}
                  className="w-full bg-zinc-900/40 border border-white/25 rounded-3xl p-5 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all resize-none text-base"
                />
                <p
                  className={`text-xs text-right pr-1 ${captionNearLimit ? 'text-orange-400' : 'text-zinc-600'}`}
                >
                  {caption.length} / {MAX_CAPTION.toLocaleString()}
                </p>
              </div>
            </div>

            {/* ── Optional (Service + Event) ───────────────────────────── */}
            <div className="flex flex-col gap-8 py-6 border-t border-white/5">
              <div className="flex items-center gap-4">
                <h3 className={sectionHeadingClass}>Optional</h3>
                <div className="h-px bg-white/5 flex-1" />
              </div>

              {/* Select Service */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-zinc-400 text-[14px] font-medium">Select Service</h4>
                  <OptionalBadge />
                </div>
                {!service ? (
                  <button
                    type="button"
                    onClick={() => setIsServicesModalOpen(true)}
                    className="w-full h-16 bg-zinc-900/40 border border-white/25 rounded-2xl px-5 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-all text-zinc-600 group"
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                        <FiPlus size={18} />
                      </div>
                      <span>Choose a service</span>
                    </div>
                    <FiPlus size={20} className="group-hover:text-white transition-colors" />
                  </button>
                ) : (
                  <div className="w-full bg-zinc-900/60 border border-blue-500/30 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center text-blue-400 shrink-0">
                      <FiBriefcase size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-blue-400 font-medium truncate">{service.title}</div>
                      {service.price && (
                        <div className="text-zinc-500 text-[13px] italic truncate">
                          {formatServicePrice(service.price)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setService(null)}
                      className="flex items-center gap-2 text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <FiTrash2 size={16} />
                      <span className="hidden sm:inline text-[13px]">Remove</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Select Event */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-zinc-400 text-[14px] font-medium">Select Event</h4>
                  <OptionalBadge />
                </div>
                {!event ? (
                  <button
                    type="button"
                    onClick={() => setIsEventsModalOpen(true)}
                    className="w-full h-16 bg-zinc-900/40 border border-white/25 rounded-2xl px-5 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-all text-zinc-600 group"
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                        <FiPlus size={18} />
                      </div>
                      <span>Choose an event</span>
                    </div>
                    <FiPlus size={20} className="group-hover:text-white transition-colors" />
                  </button>
                ) : (
                  <div className="w-full bg-zinc-900/60 border border-blue-500/30 rounded-2xl p-4 flex items-center gap-4">
                    <img
                      src={event.image || MEDIA_FALLBACK_IMAGE_SRC}
                      alt=""
                      className="w-12 h-12 rounded-xl object-cover shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{event.name}</div>
                      {event.start && (
                        <div className="text-zinc-500 text-[13px] truncate">
                          {formatEventDate(event.start)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setEvent(null)}
                      className="flex items-center gap-2 text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <FiTrash2 size={16} />
                      <span className="hidden sm:inline text-[13px]">Remove</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Platform Links ───────────────────────────────────────── */}
            <div
              ref={platformLinksRef}
              className="flex flex-col gap-6 py-6 border-t border-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-0.75 h-4 bg-blue-500" />
                <h3 className="text-white text-[15px] font-semibold">Platform Links</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PlatformInput
                  label="Website"
                  placeholder="Enter website link"
                  value={platformLinks.website}
                  error={platformErrors.website}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, website: v }));
                    setPlatformErrors((prev) => ({ ...prev, website: undefined }));
                  }}
                  onBlur={() => validatePlatformField('website', platformLinks.website)}
                />
                <PlatformInput
                  label="Spotify"
                  placeholder="Enter Spotify link"
                  value={platformLinks.spotify}
                  error={platformErrors.spotify}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, spotify: v }));
                    setPlatformErrors((prev) => ({ ...prev, spotify: undefined }));
                  }}
                  onBlur={() => validatePlatformField('spotify', platformLinks.spotify)}
                />
                <PlatformInput
                  label="SoundCloud"
                  placeholder="Enter SoundCloud link"
                  value={platformLinks.soundcloud}
                  error={platformErrors.soundcloud}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, soundcloud: v }));
                    setPlatformErrors((prev) => ({ ...prev, soundcloud: undefined }));
                  }}
                  onBlur={() => validatePlatformField('soundcloud', platformLinks.soundcloud)}
                />
                <PlatformInput
                  label="Apple Music"
                  placeholder="Enter Apple Music link"
                  value={platformLinks.appleMusic}
                  error={platformErrors.appleMusic}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, appleMusic: v }));
                    setPlatformErrors((prev) => ({ ...prev, appleMusic: undefined }));
                  }}
                  onBlur={() => validatePlatformField('appleMusic', platformLinks.appleMusic)}
                />
                <PlatformInput
                  label="Amazon Music"
                  placeholder="Enter Amazon Music link"
                  value={platformLinks.amazonMusic}
                  error={platformErrors.amazonMusic}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, amazonMusic: v }));
                    setPlatformErrors((prev) => ({ ...prev, amazonMusic: undefined }));
                  }}
                  onBlur={() => validatePlatformField('amazonMusic', platformLinks.amazonMusic)}
                />
                <PlatformInput
                  label="YouTube Music"
                  placeholder="Enter YouTube Music link"
                  value={platformLinks.youtubeMusic}
                  error={platformErrors.youtubeMusic}
                  onChange={(v) => {
                    setPlatformLinks((prev) => ({ ...prev, youtubeMusic: v }));
                    setPlatformErrors((prev) => ({ ...prev, youtubeMusic: undefined }));
                  }}
                  onBlur={() => validatePlatformField('youtubeMusic', platformLinks.youtubeMusic)}
                />
              </div>
            </div>
          </div>

          {/* ── Right Column: Collaborators ──────────────────────────────── */}
          <div className="w-full lg:w-100 shrink-0">
            <CollaboratorsSelector
              selectedIds={collaboratorIds}
              initialSelected={initialData?.initialCollaborators ?? []}
              onToggle={(id) =>
                setCollaboratorIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                )
              }
            />
          </div>
        </div>

        {/* ── Mobile action buttons ────────────────────────────────────── */}
        <div className="flex sm:hidden flex-col gap-4 pt-8 border-t border-white/5">
          <Button
            type="submit"
            size="lg"
            disabled={isLoading}
            className="w-full rounded-full h-14 bg-white text-black hover:bg-zinc-200 focus:ring-0 disabled:opacity-50"
          >
            {isLoading ? 'Saving…' : submitLabel}
          </Button>
          {showDelete && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full rounded-full h-14 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0 disabled:opacity-50"
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                  Deleting…
                </span>
              ) : (
                'Delete'
              )}
            </Button>
          )}
        </div>
      </form>

      {/* ── Upload / saving overlay (mirrors mobile progress overlay) ────── */}
      {isLoading && (
        <div className="fixed inset-0 z-200 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-white/10 rounded-4xl p-8 flex flex-col items-center gap-6 mx-4 w-full max-w-sm shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
              <MdCloudUpload size={32} className="text-blue-500" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-lg">Uploading Media</p>
              <p className="text-zinc-500 text-sm mt-1">Please wait…</p>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-full" />
            </div>
          </div>
        </div>
      )}

      {/* ── Video preview modal ──────────────────────────────────────────── */}
      {previewItem !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setPreviewItem(null)}
        >
          <button
            type="button"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewItem(null);
            }}
          >
            <FiX size={20} />
          </button>
          <video
            key={previewItem.id}
            src={previewItem.kind === 'new' ? previewItem.blobUrl : previewItem.url}
            className="max-w-[90vw] max-h-[80vh] rounded-2xl shadow-2xl outline-none"
            controls
            autoPlay
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {isDeleteModalOpen && onDelete && (
        <DeleteMediaModal
          isLoading={isDeleting}
          onConfirm={() => {
            onDelete();
          }}
          onCancel={() => {
            if (!isDeleting) setIsDeleteModalOpen(false);
          }}
        />
      )}

      {isServicesModalOpen && (
        <ServicesModal
          selectedId={service?.id}
          onSelect={(s) => {
            setService(s);
            setIsServicesModalOpen(false);
          }}
          onClose={() => setIsServicesModalOpen(false)}
        />
      )}

      {isEventsModalOpen && (
        <EventsModal
          selectedId={event?.id}
          onSelect={(e) => {
            setEvent(e);
            setIsEventsModalOpen(false);
          }}
          onClose={() => setIsEventsModalOpen(false)}
        />
      )}
    </div>
  );
};
